import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Reveal from "@/components/base/Reveal";
import ArticleCard from "@/components/feature/ArticleCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

const sortOptions = [
  { id: "heat", label: "按热度" },
  { id: "latest", label: "按最新" },
  { id: "discussed", label: "按讨论量" },
];

export default function Square() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") ?? "all";
  const query = searchParams.get("q") ?? "";
  const [sort, setSort] = useState("heat");
  const [keyword, setKeyword] = useState(query);

  const filtered = useMemo(() => {
    let list = [...articles];
    if (activeCategory !== "all") {
      list = list.filter((item) => item.categoryId === activeCategory);
    }
    const q = query.trim();
    if (q) {
      list = list.filter(
        (item) =>
          item.title.includes(q) ||
          item.excerpt.includes(q) ||
          item.tags.some((tag) => tag.includes(q)) ||
          item.category.includes(q),
      );
    }
    if (sort === "heat") list.sort((a, b) => b.heat - a.heat);
    if (sort === "discussed") list.sort((a, b) => b.comments - a.comments);
    if (sort === "latest") list.sort((a, b) => (a.date < b.date ? 1 : -1));
    return list;
  }, [activeCategory, query, sort]);

  const handleCategory = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id === "all") next.delete("category");
    else next.set("category", id);
    setSearchParams(next);
  };

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (keyword.trim()) next.set("q", keyword.trim());
    else next.delete("q");
    setSearchParams(next);
  };

  const currentCategory =
    categories.find((cat) => cat.id === activeCategory) ?? { name: "全部爆料", desc: "" };

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "511爆料广场 | 热门瓜料与匿名投稿线索",
      url: "https://cqkczx.cn/square",
      inLanguage: "zh-CN",
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: filtered.length,
        itemListElement: filtered.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `https://cqkczx.cn/article/${item.id}`,
          name: item.title,
        })),
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "首页",
          item: "https://cqkczx.cn/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "爆料广场",
          item: "https://cqkczx.cn/square",
        },
      ],
    },
  ];

  return (
    <>
      <Seo
        title="爆料广场 | 511爆料热门瓜料与匿名投稿线索"
        description="511爆料广场汇集娱乐圈、网红圈、商界风闻、职场爆料等领域的匿名投稿线索，支持按分类、热度与关键词筛选，状态标签实时更新，理性吃瓜。"
        canonical="https://cqkczx.cn/square"
        keywords="511爆料广场,511爆料瓜料,匿名投稿线索,娱乐圈爆料,网红圈爆料"
        jsonLd={jsonLd}
      />

      <section className="w-full bg-background-100/70 border-b border-background-200/70">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-8 pt-6 pb-10 md:pb-12">
          <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "爆料广场" }]} />
          <h1 className="mt-5 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
            爆料广场
          </h1>
          <p className="mt-3 max-w-3xl text-sm md:text-base text-foreground-600 leading-relaxed">
            这里汇集了来自 511爆料匿名投稿的线索。所有内容均标注核实状态，热度与讨论量随发酵进度实时变化，
            请理性吃瓜，尊重当事人隐私。
          </p>

          <form onSubmit={handleSearch} className="mt-6 flex flex-col sm:flex-row gap-2.5 max-w-2xl">
            <div className="flex-1 flex items-center gap-2 bg-background-50 border border-background-200/80 rounded-md px-4 h-11">
              <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line"></i>
              </span>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="输入关键词，例如：换角、退款、片场"
                className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                aria-label="广场内搜索"
              />
            </div>
            <button
              type="submit"
              className="h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              搜索
            </button>
          </form>
        </div>
      </section>

      <section className="w-full max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="分类筛选">
            <button
              type="button"
              role="tab"
              aria-selected={activeCategory === "all"}
              onClick={() => handleCategory("all")}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                activeCategory === "all"
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              全部
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                role="tab"
                aria-selected={activeCategory === cat.id}
                onClick={() => handleCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  activeCategory === cat.id
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <span className="text-sm text-foreground-500 whitespace-nowrap">排序</span>
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-100">
              {sortOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setSort(option.id)}
                  className={`px-3 py-1.5 rounded-full text-xs md:text-sm whitespace-nowrap transition-colors ${
                    sort === option.id
                      ? "bg-background-50 text-foreground-950 font-medium"
                      : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-foreground-500">
          <span>
            共 <strong className="text-primary-600">{filtered.length}</strong> 条线索
          </span>
          {query && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs">
              关键词：{query}
              <button
                type="button"
                aria-label="清除关键词"
                onClick={() => {
                  setKeyword("");
                  const next = new URLSearchParams(searchParams);
                  next.delete("q");
                  setSearchParams(next);
                }}
                className="w-4 h-4 flex items-center justify-center hover:text-primary-600"
              >
                <i className="ri-close-line"></i>
              </button>
            </span>
          )}
          {activeCategory !== "all" && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs">
              分类：{currentCategory.name}
            </span>
          )}
        </div>

        {filtered.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {filtered.map((article, index) => (
              <Reveal key={article.id} delay={(index % 3) * 70}>
                <ArticleCard article={article} />
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="mt-12 py-16 rounded-lg bg-background-100 border border-background-200/80 text-center">
            <span className="mx-auto w-14 h-14 rounded-full bg-background-200/80 flex items-center justify-center text-foreground-400">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-900">
              没有找到匹配的线索
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              换个关键词试试，或者直接把你手上的瓜料投给我们。
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <button
                type="button"
                onClick={() => {
                  setKeyword("");
                  setSearchParams({});
                }}
                className="px-5 py-2.5 rounded-md border border-background-300 text-foreground-700 text-sm font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors"
              >
                重置筛选
              </button>
              <Link
                to="/submit"
                className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
              >
                我要爆料
              </Link>
            </div>
          </div>
        )}
      </section>
    </>
  );
}